/**
 * Analytics Helpers - Gather enriched user data for Amplitude
 * Plan A: Essential User Context
 */

import { languages$, xp$, cards$ } from '../store';
import { supabase } from './supabase';

/**
 * Gather enriched user data for analytics identification
 * Fetches data from stores and database
 * @param {object} user - User object from auth
 * @returns {Promise<object>} Enriched user data
 */
export async function gatherEnrichedUserData(user) {
  if (!user) return {};

  try {
    const enrichedData = {};

    // Learning Context - from languages$ store
    const languages = languages$.items.get() || [];
    const selectedCode = languages$.selectedCode.get();
    const currentLanguage = languages.find(l => l.language_code === selectedCode);
    
    if (currentLanguage) {
      enrichedData.current_language_code = currentLanguage.language_code;
      enrichedData.current_language_level = currentLanguage.level;
      enrichedData.native_language = currentLanguage.native_language || 'en';
    }
    enrichedData.total_languages = languages.length;

    // Engagement Metrics - from xp$ and cards$ stores
    const totalXP = xp$.totalXP.get() || 0;
    const streak = xp$.streak.get() || 0;
    const cards = cards$.items.get() || [];
    
    enrichedData.total_cards = cards.length;
    enrichedData.total_xp = totalXP;
    enrichedData.current_streak = streak;

    // Days since signup
    if (user.created_at) {
      const createdDate = new Date(user.created_at);
      const now = new Date();
      const daysSinceSignup = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      enrichedData.days_since_signup = daysSinceSignup;
    }

    // Monetization - fetch from database
    const { data: creditsData } = await supabase
      .from('user_permanent_credits')
      .select('credit_balance')
      .eq('user_id', user.id)
      .maybeSingle();
    
    enrichedData.credit_balance = creditsData?.credit_balance ?? 1000;

    // Check if user has ever purchased credits
    const { data: purchaseData } = await supabase
      .from('reedle_credit_purchases')
      .select('credits_purchased')
      .eq('user_id', user.id)
      .eq('status', 'completed');

    const totalPurchased = purchaseData?.reduce((sum, p) => sum + (p.credits_purchased || 0), 0) || 0;
    enrichedData.total_credits_purchased = totalPurchased;
    enrichedData.is_paying_user = totalPurchased > 0;

    return enrichedData;
  } catch (error) {
    console.error('[Analytics] Failed to gather enriched user data:', error);
    return {};
  }
}

/**
 * Get current language context for event properties
 * @returns {object} Language context
 */
export function getCurrentLanguageContext() {
  const selectedCode = languages$.selectedCode.get();
  const languages = languages$.items.get() || [];
  const currentLanguage = languages.find(l => l.language_code === selectedCode);
  
  if (!currentLanguage) return {};
  
  return {
    language_code: currentLanguage.language_code,
    language_level: currentLanguage.level,
  };
}

